import React, { useMemo } from 'react';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import './DbEnumCellEditor.css';

const NULL_KEY = '\u0000null';
const key = (v) => `v:${v}`;

/**
 * An ENUM value chosen with the app's picker, for the places that are a form
 * rather than a cell being edited in place: a NEW (pending) row's cell and the
 * Edit cell dialog. `value` is the string, or null for NULL; `onChange` gets
 * the same. Values are keyed with a prefix because MySQL allows '' as an enum
 * value, which must not collide with NULL.
 *
 * `blankIsNull`: a new row's cells insert a blank (or whitespace-only) text as
 * NULL (dbGridSql.buildInsertStatement), so such a value cannot be written
 * from there — it is left out rather than offered and silently saved as NULL.
 */
export default function DbEnumSelect({
  meta,
  value,
  onChange,
  columnName,
  className = 'dbm-enum-pending',
  blankIsNull = false,
}) {
  const options = useMemo(() => {
    const values = blankIsNull ? meta.values.filter((v) => v.trim() !== '') : meta.values;
    const list = values.map((v) => ({ value: key(v), label: v === '' ? '(empty)' : v }));
    return meta.nullable ? [{ value: NULL_KEY, label: 'NULL' }, ...list] : list;
  }, [meta, blankIsNull]);
  const current = value == null ? (meta.nullable ? NULL_KEY : null) : key(value);
  return (
    <CompactSelectPicker
      className={className}
      menuClassName="dbm-enum-menu"
      value={current}
      options={options}
      placeholder="Choose…"
      searchable={options.length > 8}
      menuMinWidth={140}
      aria-label={`Value for ${columnName}`}
      onChange={(k) => onChange(k === NULL_KEY ? null : String(k).slice(2))}
    />
  );
}
